import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';
import { v4 as uuidv4 } from 'uuid';

import { AdditionalFields } from '../../models/models';
import { getDateModified } from '../../utilities/utilityFunctions';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface TaskList {
  readonly pkAssetInspectionTask: number;
  readonly fkSchedule: number;
  readonly fkAssetInspection: number;
  readonly pkUuId: string;
  readonly isActive: boolean;
  readonly dateCreated?: string;
  readonly dateModified?: string;
  readonly fkInspectorLead: number;
  readonly fkInspectorField?: number;
  readonly name?: string;
  readonly taskDescription: string;
  readonly isReviewedByLI: boolean;
  readonly isReviewedByFI: boolean;
  readonly commentInspectorLead: string;
  commentInspectorField: string;
  readonly commentSiteCoordinator: string;
  readonly site: number;
  readonly discipline: number;
  readonly modifiedBy: string;
  readonly createdBy: string;
  readonly fkInspectionTaskStatusCategory: number;
  attentionFlag: boolean;
  readonly isAssignedBack: boolean;
  readonly fieldInspectorUserId: number;
  readonly siteCoordinatorUserId: number;
  readonly recommendations?: string;
  dateCompleted?: string;
  role?: number;
  leadInspectorName: string;
  readonly clerkId?: number;
  readonly clerkName?: string;
  proxyInspector?: string;
}

export const taskListSchema: RxJsonSchema<TaskList> = {
  type: 'object',
  version: 3,
  description: 'describes deficiency lists',
  primaryKey: 'pkUuId',
  properties: {
    pkAssetInspectionTask: { type: 'number' },
    fkSchedule: { type: ['number', 'null'] },
    fkAssetInspection: { type: ['number', 'null'] },
    pkUuId: { type: 'string', maxLength: 36 },
    isActive: { type: ['boolean', 'null'] },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    fkInspectorLead: { type: ['number', 'null'] },
    fkInspectorField: { type: ['number', 'null'] },
    name: { type: ['string', 'null'] },
    taskDescription: { type: ['string', 'null'] },
    isReviewedByLI: { type: ['boolean', 'null'] },
    isReviewedByFI: { type: ['boolean', 'null'] },
    commentInspectorLead: { type: ['string', 'null'] },
    commentInspectorField: { type: ['string', 'null'] },
    commentSiteCoordinator: { type: ['string', 'null'] },
    site: { type: ['number', 'null'] },
    discipline: { type: ['number', 'null'] },
    modifiedBy: { type: ['string', 'null'] },
    createdBy: { type: ['string', 'null'] },
    fkInspectionTaskStatusCategory: { type: ['number', 'null'] },
    attentionFlag: { type: ['boolean', 'null'] },
    isAssignedBack: { type: ['boolean', 'null'] },
    fieldInspectorUserId: { type: ['number', 'null'] },
    siteCoordinatorUserId: { type: ['number', 'null'] },
    recommendations: { type: ['string', 'null'] },
    dateCompleted: { type: ['string', 'null'] },
    role: { type: ['number', 'null'] },
    leadInspectorName: { type: ['string', 'null'] },
    clerkId: { type: ['number', 'null'] },
    clerkName: { type: ['string', 'null'] },
    proxyInspector: { type: ['string', 'null'] },
  },
};

export const taskListSchemaMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: TaskList) => {
    const newDoc = {
      role: 7,
      ...oldDoc,
    };
    return newDoc;
  },
  2: (oldDoc: TaskList) => {
    const newDoc = {
      ...oldDoc,
      leadInspectorName: '',
    };
    return newDoc;
  },
  3: (oldDoc: TaskList) => {
    const newDoc = {
      ...oldDoc,
      clerkId: oldDoc.clerkId || null,
      clerkName: oldDoc.clerkName || null,
      proxyInspector: oldDoc.proxyInspector || null,
    };
    return newDoc;
  },
};

export type TaskListCollection = RxCollection<TaskList> | null;
export type TaskListDocument = RxDocument<TaskList>;

export const taskListPullQueryBuilder = (document: TaskList) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query mobileAssetInspectionTaskListInfo(
      $siteId: Int!
      $disciplineId: Int!
      $lastRequested: DateTime!
      $isLeadInspector: Boolean!
    ) {
      mobileAssetInspectionTaskListInfo(
        siteId: $siteId
        disciplineId: $disciplineId
        lastRequested: $lastRequested
        isLeadInspector: $isLeadInspector
      ) {
        pkAssetInspectionTask
        fkSchedule
        fkAssetInspection
        fkInspectorLead
        fkInspectorField
        name
        taskDescription
        isActive
        isReviewedByLI
        isReviewedByFI
        commentInspectorLead
        commentInspectorField
        commentSiteCoordinator
        dateCreated
        dateModified
        site
        discipline
        modifiedBy
        createdBy
        pkUuId
        fkInspectionTaskStatusCategory
        attentionFlag
        isAssignedBack
        fieldInspectorUserId
        siteCoordinatorUserId
        recommendations
        dateCompleted
        leadInspectorName
        clerkId
        clerkName
        proxyInspector
      }
    }
  `);

  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastRequested: getDateModified(
        doc?.dateModified,
        doc?.dateCreated,
        doc.site,
        doc.discipline,
        JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
        JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
      ),
      isLeadInspector: getValueFromLocalStorage('isLeadInspector') === 'True',
    },
  };
};

export const taskListPushQueryBuilder = (doc: TaskListDocument[]) => {
  const document = doc[0];
  const query = print(gql`
    mutation mobileFIAddOrUpdateInspection(
      $role: Int!
      $MobileFieldInspectorInspectionsInput: MobileFieldInspectorInspectionsInput
    ) {
      mobileFIAddOrUpdateInspection(
        role: $role
        mobileFieldInspectorInspections: $MobileFieldInspectorInspectionsInput
      ) {
        id
        pk_uuid
        status
      }
    }
  `);
  const { role, ...data } = document;
  const variables = {
    role: role ? role : 7,
    MobileFieldInspectorInspectionsInput: data,
  };
  return {
    query,
    variables,
  };
};

export const taskListPullModifier = (doc: TaskList) => {
  const result = {
    ...doc,
  };
  if (!result.pkUuId) {
    result.pkUuId = uuidv4();
  }
  return result;
};

export const taskListPushModifier = (doc: TaskListDocument) => {
  const obj: TaskListDocument &
    AdditionalFields & {
      isPerformedByFI: boolean;
      isBulkReAssignment: boolean;
    } = {
    ...doc,
    isPerformedByFI: false,
    isBulkReAssignment: false,
  };
  const blackListedProp = [
    '_attachments',
    '_rev',
    '_deleted',
    'name',
    'siteCoordinatorUserId',
    'fieldInspectorUserId',
    '_meta',
    'isDeleted',
    'leadInspectorName',
  ];
  blackListedProp.forEach(property => {
    obj[property as keyof AdditionalFields] = undefined;
  });
  return obj;
};

export default {};
